import { useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/feature/AdminLayout';
import Modal from '@/components/base/Modal';
import { ErrorState, EmptyState } from '@/components/feature/AsyncState';
import { useAuth } from '@/context/AuthContext';
import { useInventory } from '@/hooks/useInventory';
import { useActiveReservations } from '@/hooks/useActiveReservations';
import { useReservationSweepStatus } from '@/hooks/useReservationSweepStatus';
import { useReservationSweepHistory } from '@/hooks/useReservationSweepHistory';
import { useProducts } from '@/hooks/useProducts';
import { downloadCsv } from '@/lib/csv';
import type { InventoryItem, ActiveReservation } from '@/services/inventory';
import { releaseExpiredReservations, previewExpiredReservations } from '@/services/inventory';
import { cancelOrderAndRelease } from '@/services/orders';
import StockAdjustModal from './components/StockAdjustModal';

const STATUS_LABELS: Record<string, string> = {
  pending_payment: 'Pendiente de pago',
  payment_initiated: 'Pago iniciado',
};

const METHOD_LABELS: Record<string, string> = {
  cash: 'Contra entrega',
  transfer: 'Transferencia',
  payment_link: 'Pago en línea',
  sistecredito: 'Sistecrédito',
  nequi: 'Nequi',
  other: 'Otro',
};

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `hace ${Math.max(mins, 1)} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} día${days > 1 ? 's' : ''}`;
}

// Arma el enlace de WhatsApp para recordarle al cliente su reserva pendiente.
// Normaliza el teléfono (agrega el indicativo de Colombia si viene local).
function buildWhatsAppReminderUrl(
  phone: string,
  customerName: string,
  orderNumber: string,
  units: number,
): string {
  const digits = phone.replace(/\D/g, '');
  const normalized = digits.length <= 10 ? `57${digits}` : digits;
  const firstName = (customerName || '').trim().split(' ')[0] || 'cliente';
  const message =
    `Hola ${firstName}, te escribimos de Paticas con Propósito 🐾. ` +
    `Notamos que tienes el pedido ${orderNumber} pendiente de pago, con ${units} unidad(es) reservada(s) para ti. ` +
    `Si ya realizaste el pago, envíanos el comprobante por aquí; de lo contrario, pronto liberaremos la reserva ` +
    `para que otra persona pueda aprovecharla. ¡Gracias!`;
  return `https://wa.me/${normalized}?text=${encodeURIComponent(message)}`;
}

export default function InventoryPage() {
  const { profile } = useAuth();
  const isAgent = profile?.role === 'sales_agent';
  const [search, setSearch] = useState('');
  const [sizeFilter, setSizeFilter] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);
  const [adjustItem, setAdjustItem] = useState<InventoryItem | null>(null);
  const [releasing, setReleasing] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [releaseMsg, setReleaseMsg] = useState('');
  const [releaseErr, setReleaseErr] = useState('');
  const [releaseConfirmCount, setReleaseConfirmCount] = useState<number | null>(null);
  const [showReservations, setShowReservations] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<ActiveReservation | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [showSweepHistory, setShowSweepHistory] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  const { inventory, sizes, loading, error, refetch } = useInventory();
  const { data: products } = useProducts();
  const {
    reservations,
    totalUnits: reservedUnitsFromOrders,
    loading: reservationsLoading,
    refetch: refetchReservations,
  } = useActiveReservations();
  const {
    status: sweepStatus,
    loading: sweepLoading,
    refetch: refetchSweep,
  } = useReservationSweepStatus();
  const {
    runs: sweepHistory,
    loading: sweepHistoryLoading,
    refetch: refetchSweepHistory,
  } = useReservationSweepHistory();

  // Paso 1: consulta cuántos pedidos se liberarían y pide confirmación.
  async function handleReleaseClick() {
    setReleaseMsg('');
    setReleaseErr('');
    setPreviewing(true);
    const { count, error: previewErr } = await previewExpiredReservations(45);
    setPreviewing(false);
    if (previewErr) {
      setReleaseErr(previewErr);
      return;
    }
    if (count === 0) {
      setReleaseMsg('No hay reservas vencidas por liberar.');
      return;
    }
    setReleaseConfirmCount(count);
  }

  // Paso 2: confirmada la acción, se ejecuta el barrido.
  async function handleReleaseReservations() {
    setReleaseConfirmCount(null);
    setReleasing(true);
    setReleaseMsg('');
    setReleaseErr('');
    const { released, error: rlsErr } = await releaseExpiredReservations(45);
    setReleasing(false);
    if (rlsErr) {
      setReleaseErr(rlsErr);
      return;
    }
    if (released > 0) {
      setReleaseMsg(`Se liberaron las reservas de ${released} pedido(s) abandonado(s).`);
      refetch();
      refetchReservations();
    } else {
      setReleaseMsg('No había reservas vencidas por liberar.');
    }
    refetchSweep();
    refetchSweepHistory();
  }

  // Fuerza una corrida manual del barrido y refresca estado, histórico e inventario.
  async function handleRunNow() {
    setRunningNow(true);
    setReleaseMsg('');
    setReleaseErr('');
    const { released, error: runErr } = await releaseExpiredReservations(45);
    setRunningNow(false);
    refetchSweep();
    refetchSweepHistory();
    if (runErr) {
      setReleaseErr(runErr);
      return;
    }
    setReleaseMsg(
      released > 0
        ? `Barrido ejecutado: se liberaron las reservas de ${released} pedido(s).`
        : 'Barrido ejecutado: no había reservas vencidas por liberar.',
    );
    refetch();
    refetchReservations();
  }

  // Cancela el pedido pendiente y libera su reserva de stock (RPC atómico).
  async function handleCancelReservation() {
    if (!cancelTarget) return;
    setCancelling(true);
    setCancelError('');
    const target = cancelTarget;
    const { error: cancelErr } = await cancelOrderAndRelease(target.orderId);
    setCancelling(false);
    if (cancelErr) {
      setCancelError(cancelErr);
      return;
    }
    setCancelTarget(null);
    setReleaseMsg(`Pedido ${target.orderNumber} cancelado y reserva liberada.`);
    refetch();
    refetchReservations();
  }

  const filtered = inventory.filter((i) => {
    const product = products.find((p) => p.id === i.productId);
    const size = sizes.find((s) => s.id === i.sizeId);
    if (sizeFilter !== 'all' && i.sizeId !== sizeFilter) return false;
    if (showLowStock && i.quantityAvailable > i.minStock) return false;
    if (search) {
      const q = search.toLowerCase();
      if (
        !product?.name.toLowerCase().includes(q) &&
        !size?.label.toLowerCase().includes(q) &&
        !product?.sku.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  const totalPairs = inventory.reduce(
    (s, i) => s + i.quantityAvailable + i.quantityReserved + i.quantityConsigned,
    0,
  );
  const totalReserved = inventory.reduce((s, i) => s + i.quantityReserved, 0);
  const totalCost = inventory.reduce(
    (s, i) => s + (i.quantityAvailable + i.quantityReserved + i.quantityConsigned) * i.unitCost,
    0,
  );
  const totalPotential = inventory.reduce((s, i) => {
    const p = products.find((pr) => pr.id === i.productId);
    return s + (i.quantityAvailable + i.quantityReserved + i.quantityConsigned) * (p?.commercialPrice || 0);
  }, 0);

  function exportInventory() {
    downloadCsv(
      'inventario',
      ['Producto', 'SKU', 'Talla', 'Disponible', 'Reservado', 'Consignado', 'Total', 'Costo Unit.', 'Valor Venta'],
      inventory.map((i) => {
        const p = products.find((x) => x.id === i.productId);
        const s = sizes.find((x) => x.id === i.sizeId);
        const totalQty = i.quantityAvailable + i.quantityReserved + i.quantityConsigned;
        return [
          p?.name ?? '',
          p?.sku ?? '',
          s?.label ?? '',
          i.quantityAvailable,
          i.quantityReserved,
          i.quantityConsigned,
          totalQty,
          i.unitCost,
          totalQty * (p?.commercialPrice || 0),
        ];
      }),
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        {releaseErr && (
          <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{releaseErr}</span>
          </div>
        )}
        {releaseMsg && (
          <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
            <i className="ri-checkbox-circle-line text-emerald-600 text-lg"></i>
            <p className="text-sm text-emerald-800 font-medium">{releaseMsg}</p>
            <button
              onClick={() => setReleaseMsg('')}
              className="ml-auto w-7 h-7 flex items-center justify-center rounded-lg hover:bg-emerald-100 text-emerald-600 transition-colors cursor-pointer"
              aria-label="Cerrar mensaje"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}
        {/* Estado del barrido automático (cron) */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 bg-white rounded-xl border border-background-200 px-4 py-3 text-sm">
          <span className="flex items-center gap-2">
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                sweepLoading ? 'bg-background-300' : sweepStatus?.active ? 'bg-emerald-500' : 'bg-amber-500'
              }`}
            ></span>
            <i className="ri-timer-flash-line text-foreground-500"></i>
            <span className="font-medium text-foreground-900">Barrido automático</span>
          </span>
          <span className="text-foreground-600">
            {sweepLoading
              ? 'Consultando...'
              : sweepStatus
                ? sweepStatus.active
                  ? `Cada ${sweepStatus.intervalMinutes} min`
                  : 'Inactivo'
                : 'Sin datos'}
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => {
                refetchSweepHistory();
                setShowSweepHistory(true);
              }}
              title="Ver el histórico de corridas del barrido"
              className="text-xs text-foreground-600 hover:text-foreground-900 hover:bg-background-100 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-history-line"></i>
              {sweepStatus?.lastRun?.startTime
                ? `Última corrida ${relativeTime(sweepStatus.lastRun.startTime)}`
                : 'Aún sin ejecuciones registradas'}
              <i className="ri-arrow-right-s-line"></i>
            </button>
            {!isAgent && (
              <button
                onClick={handleRunNow}
                disabled={runningNow}
                title="Forzar una corrida manual del barrido ahora"
                className="text-xs px-3 py-1.5 rounded-lg bg-accent-500 text-white hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {runningNow ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Ejecutando...
                  </>
                ) : (
                  <>
                    <i className="ri-play-circle-line mr-1.5"></i>Ejecutar ahora
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Inventario</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>
                {totalPairs} pares totales · Costo: ${totalCost.toLocaleString()} · Valor potencial: ${totalPotential.toLocaleString()}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportInventory}
              className="text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-download-line mr-1.5"></i>Exportar
            </button>
            {!isAgent && (
              <>
                <button
                  onClick={handleReleaseClick}
                  disabled={releasing || previewing}
                  title="Liberar reservas de pedidos abandonados (más de 45 min sin pagar)"
                  className="text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {releasing || previewing ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-1.5"></i>
                      {previewing ? 'Consultando...' : 'Liberando...'}
                    </>
                  ) : (
                    <>
                      <i className="ri-refresh-line mr-1.5"></i>
                      Liberar reservas vencidas
                    </>
                  )}
                </button>
                <Link
                  to="/admin/movimientos"
                  className="text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-exchange-line mr-1.5"></i>Movimientos
                </Link>
                <Link
                  to="/admin/compras"
                  className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium"
                >
                  <i className="ri-add-line mr-1.5"></i>Registrar Entrada
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Total Pares</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{totalPairs}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Costo Total Inventario</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">${totalCost.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Valor Potencial de Venta</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">${totalPotential.toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Utilidad Potencial</p>
            <p className="text-xl font-bold text-accent-600 mt-1">
              ${(totalPotential - totalCost).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Reservas activas */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <button
            onClick={() => setShowReservations(!showReservations)}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-background-50 transition-colors cursor-pointer"
          >
            <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
              <i className="ri-lock-2-line"></i>
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground-950">Reservas activas de stock</p>
              <p className="text-xs text-foreground-500 mt-0.5">
                {reservationsLoading
                  ? 'Consultando reservas...'
                  : `${reservedUnitsFromOrders} unidad(es) retenidas por ${reservations.length} pedido(s) pendiente(s)`}
              </p>
            </div>
            <span className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 font-semibold whitespace-nowrap">
              {totalReserved} u. en inventario
            </span>
            <i
              className={`ri-arrow-down-s-line text-lg text-foreground-400 transition-transform ${
                showReservations ? 'rotate-180' : ''
              }`}
            ></i>
          </button>

          {showReservations && (
            <div className="border-t border-background-100">
              {reservationsLoading ? (
                <p className="px-4 py-6 text-center text-sm text-foreground-400">
                  <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando desglose...
                </p>
              ) : reservations.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-foreground-400">
                  No hay reservas activas: todo el stock disponible está libre.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-background-100 text-left bg-background-50">
                        <th className="px-5 py-2.5 text-xs font-medium text-foreground-500">Pedido</th>
                        <th className="px-5 py-2.5 text-xs font-medium text-foreground-500">Cliente</th>
                        <th className="px-5 py-2.5 text-xs font-medium text-foreground-500">Estado</th>
                        <th className="px-5 py-2.5 text-xs font-medium text-foreground-500">Método</th>
                        <th className="px-5 py-2.5 text-xs font-medium text-foreground-500 text-right">Unidades</th>
                        <th className="px-5 py-2.5 text-xs font-medium text-foreground-500">Antigüedad</th>
                        <th className="px-5 py-2.5 text-xs font-medium text-foreground-500 text-right">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reservations.map((r) => (
                        <tr key={r.orderId} className="border-b border-background-100 last:border-0 hover:bg-background-50 transition-colors">
                          <td className="px-5 py-2.5 font-medium text-foreground-900 whitespace-nowrap">{r.orderNumber}</td>
                          <td className="px-5 py-2.5 text-foreground-700">
                            <span className="block truncate max-w-[180px]">{r.customerName || 'Sin nombre'}</span>
                          </td>
                          <td className="px-5 py-2.5 whitespace-nowrap">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                              {STATUS_LABELS[r.status] ?? r.status}
                            </span>
                          </td>
                          <td className="px-5 py-2.5 text-foreground-600 whitespace-nowrap">
                            {METHOD_LABELS[r.paymentMethod] ?? (r.paymentMethod || 'Sin definir')}
                          </td>
                          <td className="px-5 py-2.5 text-right font-semibold text-foreground-900 whitespace-nowrap">
                            {r.reservedUnits}
                          </td>
                          <td className="px-5 py-2.5 text-xs text-foreground-500 whitespace-nowrap">{relativeTime(r.createdAt)}</td>
                          <td className="px-5 py-2.5 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-1.5">
                              <Link
                                to={`/admin/orders?order=${r.orderId}`}
                                title="Abrir pedido"
                                aria-label="Abrir pedido"
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                              >
                                <i className="ri-external-link-line text-sm"></i>
                              </Link>
                              {/* Nota: este enlace de WhatsApp es una acción INTERNA del admin
                                  (escribirle al cliente). A propósito NO disparamos el evento
                                  `Lead` aquí, para no mezclar contactos internos con los
                                  visitantes que llegan por anuncios. */}
                              {r.customerPhone ? (
                                <a
                                  href={buildWhatsAppReminderUrl(
                                    r.customerPhone,
                                    r.customerName,
                                    r.orderNumber,
                                    r.reservedUnits,
                                  )}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  title="Enviar recordatorio por WhatsApp"
                                  aria-label="Enviar recordatorio por WhatsApp"
                                  className="w-8 h-8 flex items-center justify-center rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer"
                                >
                                  <i className="ri-whatsapp-line text-sm"></i>
                                </a>
                              ) : (
                                <span
                                  title="El cliente no tiene teléfono registrado"
                                  aria-label="Cliente sin teléfono"
                                  className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-300 cursor-not-allowed"
                                >
                                  <i className="ri-whatsapp-line text-sm"></i>
                                </span>
                              )}
                              {!isAgent && (
                                <button
                                  onClick={() => {
                                    setCancelError('');
                                    setCancelTarget(r);
                                  }}
                                  title="Cancelar pedido y liberar reserva"
                                  aria-label="Cancelar pedido"
                                  className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                                >
                                  <i className="ri-close-circle-line text-sm"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              placeholder="Buscar por nombre, SKU o talla..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
            />
          </div>
          <select
            value={sizeFilter}
            onChange={(e) => setSizeFilter(e.target.value)}
            className="text-sm border border-background-200 rounded-lg px-3 py-2 bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
          >
            <option value="all">Todas las tallas</option>
            {sizes.filter((s) => s.active).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowLowStock(!showLowStock)}
            className={`text-sm px-4 py-2 rounded-lg border transition-colors whitespace-nowrap ${
              showLowStock
                ? 'bg-red-50 border-red-200 text-red-700'
                : 'border-background-200 text-foreground-600 hover:bg-background-100'
            }`}
          >
            <i className={`${showLowStock ? 'ri-alert-fill' : 'ri-alert-line'} mr-1.5`}></i>
            Stock Bajo
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Referencia</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Talla</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Disponible</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Reservado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Consignado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Total</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Costo Unit.</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Valor Venta</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 text-right">Utilidad</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Ubicación</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={11} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando inventario...
                    </td>
                  </tr>
                ) : (
                  filtered.map((item) => {
                    const product = products.find((p) => p.id === item.productId);
                    const size = sizes.find((s) => s.id === item.sizeId);
                    const totalQty = item.quantityAvailable + item.quantityReserved + item.quantityConsigned;
                    const totalValue = totalQty * (product?.commercialPrice || 0);
                    const totalProfit = totalValue - totalQty * item.unitCost;
                    const isLow = item.quantityAvailable <= item.minStock;
                    return (
                      <tr
                        key={item.id}
                        className={`border-b border-background-100 hover:bg-background-50 transition-colors ${
                          isLow ? 'bg-red-50/50' : ''
                        }`}
                      >
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                              {product?.images?.[0] ? (
                                <img
                                  src={product.images[0]}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <i className="ri-image-line text-foreground-400 text-xs"></i>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-foreground-900 font-medium text-sm truncate">{product?.name}</p>
                              <p className="text-xs text-foreground-500">{product?.sku}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-foreground-700 font-medium whitespace-nowrap">{size?.name}</td>
                        <td
                          className={`px-5 py-3 text-right font-medium whitespace-nowrap ${
                            isLow ? 'text-red-600' : 'text-foreground-900'
                          }`}
                        >
                          {item.quantityAvailable}
                          {isLow && <i className="ri-alert-fill text-red-500 ml-1 text-xs"></i>}
                        </td>
                        <td className="px-5 py-3 text-right text-foreground-600 whitespace-nowrap">
                          {item.quantityReserved}
                        </td>
                        <td className="px-5 py-3 text-right text-foreground-600 whitespace-nowrap">
                          {item.quantityConsigned}
                        </td>
                        <td className="px-5 py-3 text-right text-foreground-900 font-medium whitespace-nowrap">{totalQty}</td>
                        <td className="px-5 py-3 text-right text-foreground-600 whitespace-nowrap">
                          ${item.unitCost.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right text-emerald-600 font-medium whitespace-nowrap">
                          ${totalValue.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right text-accent-600 font-medium whitespace-nowrap">
                          ${totalProfit.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{item.location}</td>
                        <td className="px-5 py-3">
                          {!isAgent && (
                            <button
                              onClick={() => setAdjustItem(item)}
                              title="Ajustar stock"
                              aria-label="Ajustar stock"
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                            >
                              <i className="ri-edit-box-line"></i>
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      {!loading && !error && filtered.length === 0 && <EmptyState />}

      {cancelTarget && (
        <Modal
          title="Cancelar pedido"
          subtitle={cancelTarget.orderNumber}
          onClose={() => !cancelling && setCancelTarget(null)}
          footer={
            <>
              <button
                onClick={() => !cancelling && setCancelTarget(null)}
                className="text-sm px-4 py-2 border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Volver
              </button>
              <button
                onClick={handleCancelReservation}
                disabled={cancelling}
                className="text-sm px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors whitespace-nowrap font-medium cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {cancelling ? (
                  <><i className="ri-loader-4-line animate-spin mr-1.5"></i>Cancelando...</>
                ) : (
                  <><i className="ri-close-circle-line mr-1.5"></i>Cancelar pedido</>
                )}
              </button>
            </>
          }
        >
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-red-100 text-red-600 flex-shrink-0">
              <i className="ri-error-warning-line text-lg"></i>
            </span>
            <div className="text-sm text-foreground-700">
              <p>
                Se cancelará el pedido{' '}
                <strong className="text-foreground-950">{cancelTarget.orderNumber}</strong> de{' '}
                <strong className="text-foreground-950">{cancelTarget.customerName || 'sin nombre'}</strong> y
                se liberarán sus <strong className="text-foreground-950">{cancelTarget.reservedUnits} unidad(es)</strong> reservadas.
              </p>
              <p className="mt-2 text-xs text-foreground-500">
                El stock volverá a quedar disponible de inmediato. Solo aplica a pedidos pendientes de pago
                sin un pago aprobado.
              </p>
            </div>
          </div>
          {cancelError && (
            <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <i className="ri-error-warning-line text-red-600"></i>
              <span className="text-sm text-red-700">{cancelError}</span>
            </div>
          )}
        </Modal>
      )}

      {adjustItem && (
        <StockAdjustModal
          item={adjustItem}
          productName={products.find((p) => p.id === adjustItem.productId)?.name ?? ''}
          sizeLabel={sizes.find((s) => s.id === adjustItem.sizeId)?.label ?? ''}
          onClose={() => setAdjustItem(null)}
          onSaved={refetch}
        />
      )}

      {releaseConfirmCount !== null && (
        <Modal
          title="Liberar reservas vencidas"
          subtitle="Confirma antes de ejecutar el barrido"
          onClose={() => setReleaseConfirmCount(null)}
          footer={
            <>
              <button
                onClick={() => setReleaseConfirmCount(null)}
                className="text-sm px-4 py-2 border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                onClick={handleReleaseReservations}
                disabled={releasing}
                className="text-sm px-4 py-2 bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <i className="ri-refresh-line mr-1.5"></i>
                Liberar {releaseConfirmCount} pedido(s)
              </button>
            </>
          }
        >
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 flex-shrink-0">
              <i className="ri-lock-unlock-line text-lg"></i>
            </span>
            <div className="text-sm text-foreground-700">
              <p>
                Se liberarán las reservas de{' '}
                <strong className="text-foreground-950">{releaseConfirmCount} pedido(s)</strong> de
                checkout en línea abandonado (más de 45 min sin pagar).
              </p>
              <p className="mt-2 text-xs text-foreground-500">
                Solo afecta checkouts en línea sin pago aprobado. Nunca toca pedidos contra entrega,
                transferencia ni Nequi. Los pedidos liberados quedan como cancelados.
              </p>
            </div>
          </div>
        </Modal>
      )}

      {showSweepHistory && (
        <Modal
          title="Histórico del barrido"
          subtitle="Últimas corridas automáticas y manuales"
          onClose={() => setShowSweepHistory(false)}
          maxWidth="max-w-lg"
          footer={
            <button
              onClick={() => setShowSweepHistory(false)}
              className="text-sm px-4 py-2 border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cerrar
            </button>
          }
        >
          {sweepHistoryLoading ? (
            <p className="py-10 text-center text-sm text-foreground-400">
              <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando histórico...
            </p>
          ) : sweepHistory.length === 0 ? (
            <p className="py-10 text-center text-sm text-foreground-400">
              Aún no hay corridas registradas. La primera aparecerá en la
              próxima ejecución automática o cuando uses "Ejecutar ahora".
            </p>
          ) : (
            <ul className="space-y-2">
              {sweepHistory.map((run) => {
                const ok = run.status === 'success';
                return (
                  <li
                    key={run.id}
                    className="flex items-start gap-3 rounded-lg border border-background-200 px-3 py-2.5"
                  >
                    <span
                      className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${
                        ok ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'
                      }`}
                    >
                      <i className={ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground-900">
                          {ok ? 'Éxito' : 'Error'}
                        </span>
                        <span
                          className={`text-[11px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                            run.source === 'manual'
                              ? 'bg-accent-100 text-accent-700'
                              : 'bg-secondary-100 text-secondary-700'
                          }`}
                        >
                          {run.source === 'manual' ? 'Manual' : 'Automático'}
                        </span>
                        <span className="text-xs text-foreground-500 ml-auto whitespace-nowrap">
                          {relativeTime(run.createdAt)}
                        </span>
                      </div>
                      <p className="text-xs text-foreground-600 mt-1">
                        {ok
                          ? run.releasedCount > 0
                            ? `Liberó ${run.releasedCount} pedido(s)`
                            : 'Sin reservas vencidas por liberar'
                          : run.message || 'La corrida falló'}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Modal>
      )}
      </div>
    </AdminLayout>
  );
}